import { ArrowUp, ChevronRight, FileText, RefreshCcw } from 'lucide-react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';

type Variant = 'BILL_PAY' | 'EXTERNAL_TRANSFER' | 'INTERNAL_TRANSFER';

type PaymentInitiationCardProps = {
  variant: Variant;
  onInitiatePayment: () => void;
};

export const PaymentInitiationCard = ({ variant, onInitiatePayment }: PaymentInitiationCardProps) => {
  const { icon: Icon, title, description } = getPaymentInitiationCardContent(variant);

  return (
    <Button
      variant="ghost"
      size="none"
      onClick={onInitiatePayment}
      aria-label={title}
      className="w-full rounded-lg text-start"
    >
      <Inline
        space={3}
        wrap="nowrap"
        align="start"
        className="w-full self-stretch rounded-lg bg-muted px-5 py-6 hover:bg-accent"
      >
        <div className="h-10 w-10 rounded-full bg-white object-cover p-2.5">
          <Icon className="h-full w-full text-black" />
        </div>

        <Inline space={1} className="w-full justify-between">
          <Stack space={1} className="w-full flex-1">
            <h3 className="text-base font-medium text-wrap text-black">{title}</h3>

            <p className="text-start text-xs font-medium text-wrap text-muted-foreground">{description}</p>
          </Stack>

          <div className="h-6 w-6">
            <ChevronRight className="h-full w-full object-cover text-black" />
          </div>
        </Inline>
      </Inline>
    </Button>
  );
};

const getPaymentInitiationCardContent = (variant: Variant) => {
  switch (variant) {
    case 'BILL_PAY':
      return {
        icon: FileText,
        title: 'Pay an invoice',
        description: `Upload an invoice and we'll help you with the rest`,
      };
    case 'EXTERNAL_TRANSFER':
      return {
        icon: ArrowUp,
        title: 'Send money',
        description: 'Make a one-off payment to any recipient',
      };
    case 'INTERNAL_TRANSFER':
      return {
        icon: RefreshCcw,
        title: 'Transfer internally',
        description: 'Move money between your company accounts',
      };
    default:
      return exhaustiveCheck(variant);
  }
};
